<template>
  <v-col cols="6" id="3D_renderer">
    <!--  3d viewer -->
    The 3D viewer is only here to help you visualize the shape. It is NOT a
    faithful representation of reality, and only the numbers of the form should
    be trusted.
    <br />
    <button
      @click="
        (e) => {
          e.preventDefault()
          exportGLTF(true)
        }
      "
    >
      Download 3D model
    </button>
  </v-col>
</template>
<script>
import * as THREE from 'three'
import dataTools from '../../tools/data'
import { mapGetters } from 'vuex'
import { GLTFExporter } from '../../../node_modules/three/examples/jsm/exporters/GLTFExporter.js'

export default {
  name: 'exo-3d-viewer',
  props: {
    root: Object,
    shapeId: String
  },
  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentMission: 'currentMission'
    }),

    isBoxShape() {
      return this.inputs.type === 'BOX'
    },

    inputsWatcher() {
      return JSON.stringify(this.root.inputs)
    }
  },

  watch: {
    inputsWatcher: function (value) {
      setTimeout(() => {
        this.rebuild()
      }, 1000)
    }
  },
  mounted() {
    setTimeout(() => {
      this.create3DRenderer()
      this.$root.$on('before-clearing-form', this.beforeClearingFormHandler)
      this.$root.$on('base-object-updated', this.baseObjectUpdatedHandler)
    }, 10)
  },

  beforeDestroy() {
    this.$root.$off('before-clearing-form', this.beforeClearingFormHandler)
    this.$root.$off('base-object-updated', this.baseObjectUpdatedHandler)
  },

  data() {
    return {
      basicLength: 150, // Threejs is counting everything in pixels. This is the basic length we found that render "nice" at screen
      basicThrusterRadius: 15,
      basicAntennaRadius: 2.5,
      basicPayloadRadius: 10,
      basicShaftRadius: 1,
      tipArrowSize: 40,
      axisNameOffset: 10,
      lengthArrowOutsideSpacecraft: 170,
      xColor: 0xff0000,
      yColor: 0x00ff00,
      zColor: 0x0000ff,
      renderer: new THREE.WebGLRenderer({ alpha: true }),
      camera: null,
      scene: null,
      cubeX: null,
      cubeY: null,
      cubeZ: null,
      shaft: null,
      pannel: null,
      cube: null,
      cubeGeometry: null,
      requestId: null,
      savedShapeId: ''
    }
  },

  methods: {
    beforeClearingFormHandler() {
      this.savedShapeId = this.root.inputs.id
    },
    baseObjectUpdatedHandler() {
      this.exportGLTF()
    },

    rebuild() {
      document.getElementById('3DModel').remove()
      cancelAnimationFrame(this.requestId)
      this.create3DRenderer()
    },

    createBasicMaterial() {
      const material = new THREE.MeshPhongMaterial({
        color: 0x929292,
        specular: 10,
        shininess: 10,
        flatShading: THREE.SmoothShading,
        vertexColors: THREE.FaceColors
      })

      return material
    },

    createPayloadMaterial() {
      const payloadMaterial = new THREE.MeshPhongMaterial({
        color: 'red',

        shininess: 10,
        flatShading: THREE.SmoothShading,
        vertexColors: THREE.FaceColors
      })
      return payloadMaterial
    },
    createAntennaMaterial() {
      const antennaMaterial = new THREE.MeshPhongMaterial({
        color: 'green',

        shininess: 10,
        flatShading: THREE.SmoothShading,
        vertexColors: THREE.FaceColors
      })
      return antennaMaterial
    },
    createThrusterMaterial() {
      const thrusterMaterial = [
        //sideMaterial
        new THREE.MeshPhongMaterial({
          color: 0x929292
        }),
        //topMaterial
        new THREE.MeshStandardMaterial({
          map: new THREE.TextureLoader().load(
            require('../../assets/images/hall-thruster.jpg')
          ),
          metalness: 0,
          roughness: 0.4
        }),
        //bottomMaterial
        new THREE.MeshPhongMaterial({
          color: 0x929292
        })
      ]

      return thrusterMaterial
    },

    createPannelMaterial() {
      const pannelMaterial = [
        new THREE.MeshPhongMaterial({
          color: 0x929292
        }),
        new THREE.MeshPhongMaterial({
          color: 0x929292
        }),
        new THREE.MeshPhongMaterial({
          color: 0x929292
        }),
        new THREE.MeshPhongMaterial({
          color: 0x929292
        }),
        new THREE.MeshPhongMaterial({
          map: new THREE.TextureLoader().load(
            require('../../assets/images/solar-cells.jpg')
          ),
          shininess: 40,
          flatShading: THREE.SmoothShading
        }),

        new THREE.MeshPhongMaterial({
          color: 0x929292
        })
      ]

      return pannelMaterial
    },

    objectReset(object) {
      object.rotation.x = 0
      object.rotation.y = 0
      object.rotation.z = 0

      object.scale.x = 1
      object.scale.y = 1
      object.scale.z = 1
    },

    payloadManagement(payload) {
      let vectorPayload = new THREE.Vector3(
        parseFloat(this.root.inputs.payloadPointingAxisInSatelliteFrameX),
        parseFloat(this.root.inputs.payloadPointingAxisInSatelliteFrameY),
        parseFloat(this.root.inputs.payloadPointingAxisInSatelliteFrameZ)
      )
      let vectorAbsPayload = new THREE.Vector3(
        parseFloat(
          Math.abs(this.root.inputs.payloadPointingAxisInSatelliteFrameX)
        ),
        parseFloat(
          Math.abs(this.root.inputs.payloadPointingAxisInSatelliteFrameY)
        ),
        parseFloat(
          Math.abs(this.root.inputs.payloadPointingAxisInSatelliteFrameZ)
        )
      )

      vectorPayload.normalize()
      vectorAbsPayload.normalize()
      let widthOutsideSpacecraft = this.basicLength / 3
      let scaleFactor = this.findDistanceToCubeSurface(
        vectorAbsPayload,
        this.cubeX,
        this.cubeY,
        this.cubeZ,
        this.basicPayloadRadius,
        widthOutsideSpacecraft
      )

      let lambdap = Math.atan2(vectorPayload.y, vectorPayload.x)
      let phip = -Math.asin(vectorPayload.z)

      payload.rotation.x = 0
      payload.rotation.y = 0
      payload.rotation.z = 0

      let q1p = new THREE.Quaternion()
      q1p.setFromAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 2)
      let q2p = new THREE.Quaternion()
      q2p.setFromAxisAngle(new THREE.Vector3(0, 1, 0), phip)
      let q3p = new THREE.Quaternion()
      q3p.setFromAxisAngle(new THREE.Vector3(0, 0, 1), lambdap)

      let qp = q3p.multiply(q2p).multiply(q1p)
      payload.setRotationFromQuaternion(qp)

      payload.scale.y = scaleFactor / this.basicLength
    },

    antennaManagement(antenna) {
      let vectorAntenna = new THREE.Vector3(
        parseFloat(this.root.inputs.antennaPointingAxisInSatelliteFrameX),
        parseFloat(this.root.inputs.antennaPointingAxisInSatelliteFrameY),
        parseFloat(this.root.inputs.antennaPointingAxisInSatelliteFrameZ)
      )
      let vectorAbsAntenna = new THREE.Vector3(
        parseFloat(
          Math.abs(this.root.inputs.antennaPointingAxisInSatelliteFrameX)
        ),
        parseFloat(
          Math.abs(this.root.inputs.antennaPointingAxisInSatelliteFrameY)
        ),
        parseFloat(
          Math.abs(this.root.inputs.antennaPointingAxisInSatelliteFrameZ)
        )
      )

      vectorAntenna.normalize()
      vectorAbsAntenna.normalize()
      let widthOutsideSpacecraft = (2 * this.basicLength) / 3
      let scaleFactor = this.findDistanceToCubeSurface(
        vectorAbsAntenna,
        this.cubeX,
        this.cubeY,
        this.cubeZ,
        this.basicAntennaRadius,
        widthOutsideSpacecraft
      )

      let lambdap = Math.atan2(vectorAntenna.y, vectorAntenna.x)
      let phip = -Math.asin(vectorAntenna.z)

      antenna.rotation.x = 0
      antenna.rotation.y = 0
      antenna.rotation.z = 0

      let q1p = new THREE.Quaternion()
      q1p.setFromAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 2)
      let q2p = new THREE.Quaternion()
      q2p.setFromAxisAngle(new THREE.Vector3(0, 1, 0), phip)
      let q3p = new THREE.Quaternion()
      q3p.setFromAxisAngle(new THREE.Vector3(0, 0, 1), lambdap)

      let qp = q3p.multiply(q2p).multiply(q1p)
      antenna.setRotationFromQuaternion(qp)

      antenna.scale.y = scaleFactor / this.basicLength
    },

    mouseMovingCube(cube, renderer) {
      let isDragging = false
      let previousMousePosition = {
        x: 0,
        y: 0
      }
      let domElement = renderer.domElement
      domElement.addEventListener('mousedown', function (e) {
        isDragging = true
      })
      domElement.addEventListener('mousemove', function (e) {
        let deltaMove = {
          x: e.offsetX - previousMousePosition.x,
          y: e.offsetY - previousMousePosition.y
        }

        if (isDragging) {
          let deltaRotationQuaternion = new THREE.Quaternion().setFromEuler(
            new THREE.Euler(
              dataTools.deg2rad(deltaMove.y * 1),
              dataTools.deg2rad(deltaMove.x * 1),
              0,
              'XYZ'
            )
          )

          cube.quaternion.multiplyQuaternions(
            deltaRotationQuaternion,
            cube.quaternion
          )
        }

        previousMousePosition = {
          x: e.offsetX,
          y: e.offsetY
        }
      })

      document.addEventListener('mouseup', function (e) {
        isDragging = false
      })

      // shim layer with setTimeout fallback
      window.requestAnimFrame = (function () {
        return (
          window.requestAnimationFrame ||
          window.webkitRequestAnimationFrame ||
          window.mozRequestAnimationFrame ||
          function (callback) {
            window.setTimeout(callback, 1000 / 60)
          }
        )
      })()
    },

    pannelRotatingManagement(shaft, pannel) {
      this.objectReset(shaft)

      pannel.rotation.y = 0

      let vectorShaft = new THREE.Vector3(
        parseFloat(this.root.inputs.solarArrayAxisInSatelliteFrameX),
        parseFloat(this.root.inputs.solarArrayAxisInSatelliteFrameY),
        parseFloat(this.root.inputs.solarArrayAxisInSatelliteFrameZ)
      )

      let vectorAbsShaft = new THREE.Vector3(
        parseFloat(Math.abs(this.root.inputs.solarArrayAxisInSatelliteFrameX)),
        parseFloat(Math.abs(this.root.inputs.solarArrayAxisInSatelliteFrameY)),
        parseFloat(Math.abs(this.root.inputs.solarArrayAxisInSatelliteFrameZ))
      )
      let widthOutsideSpacecraft = (2 * this.basicLength) / 3
      vectorShaft.normalize()
      vectorAbsShaft.normalize()
      let scaleFactor = this.findDistanceToCubeSurface(
        vectorAbsShaft,
        this.cubeX,
        this.cubeY,
        this.cubeZ,
        this.basicShaftRadius,
        widthOutsideSpacecraft
      )

      let lambda = Math.atan2(vectorShaft.y, vectorShaft.x)
      let phi = -Math.asin(vectorShaft.z)

      let q1 = new THREE.Quaternion()
      q1.setFromAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 2)
      let q2 = new THREE.Quaternion()
      q2.setFromAxisAngle(new THREE.Vector3(0, 1, 0), phi)
      let q3 = new THREE.Quaternion()
      q3.setFromAxisAngle(new THREE.Vector3(0, 0, 1), lambda)

      let q = q3.multiply(q2).multiply(q1)
      shaft.setRotationFromQuaternion(q)
      shaft.scale.y = scaleFactor / this.basicLength
    },

    pannelBodyManagement(cube, cubeGeometry) {
      const color = 'blue'

      for (let i = 0; i < this.root.inputs.bodySolarArrayFaces.length; i++) {
        if (this.root.inputs.bodySolarArrayFaces[i] === 'PLUS_X') {
          cube.geometry.faces[0].color = cube.geometry.faces[1].color =
            new THREE.Color(color)
          cubeGeometry.elementsNeedUpdate = true
        } else if (this.root.inputs.bodySolarArrayFaces[i] === 'MINUS_X') {
          cube.geometry.faces[2].color = cube.geometry.faces[3].color =
            new THREE.Color(color)
          cubeGeometry.elementsNeedUpdate = true
        } else if (this.root.inputs.bodySolarArrayFaces[i] === 'PLUS_Y') {
          cube.geometry.faces[4].color = cube.geometry.faces[5].color =
            new THREE.Color(color)
          cubeGeometry.elementsNeedUpdate = true
        } else if (this.root.inputs.bodySolarArrayFaces[i] === 'MINUS_Y') {
          cube.geometry.faces[6].color = cube.geometry.faces[7].color =
            new THREE.Color(color)
          cubeGeometry.elementsNeedUpdate = true
        } else if (this.root.inputs.bodySolarArrayFaces[i] === 'PLUS_Z') {
          cube.geometry.faces[8].color = cube.geometry.faces[9].color =
            new THREE.Color(color)
          cubeGeometry.elementsNeedUpdate = true
        } else if (this.root.inputs.bodySolarArrayFaces[i] === 'MINUS_Z') {
          cube.geometry.faces[10].color = cube.geometry.faces[11].color =
            new THREE.Color(color)
          cubeGeometry.elementsNeedUpdate = true
        }
      }
      if (!this.root.inputs.bodySolarArrayFaces.includes('PLUS_X')) {
        cube.geometry.faces[0].color = cube.geometry.faces[1].color =
          new THREE.Color(0xd2d2d2)
        cubeGeometry.elementsNeedUpdate = true
      }
      if (!this.root.inputs.bodySolarArrayFaces.includes('MINUS_X')) {
        cube.geometry.faces[2].color = cube.geometry.faces[3].color =
          new THREE.Color(0xd2d2d2)
        cubeGeometry.elementsNeedUpdate = true
      }
      if (!this.root.inputs.bodySolarArrayFaces.includes('PLUS_Y')) {
        cube.geometry.faces[4].color = cube.geometry.faces[5].color =
          new THREE.Color(0xd2d2d2)
        cubeGeometry.elementsNeedUpdate = true
      }
      if (!this.root.inputs.bodySolarArrayFaces.includes('MINUS_Y')) {
        cube.geometry.faces[6].color = cube.geometry.faces[7].color =
          new THREE.Color(0xd2d2d2)
        cubeGeometry.elementsNeedUpdate = true
      }
      if (!this.root.inputs.bodySolarArrayFaces.includes('PLUS_Z')) {
        cube.geometry.faces[8].color = cube.geometry.faces[9].color =
          new THREE.Color(0xd2d2d2)
        cubeGeometry.elementsNeedUpdate = true
      }
      if (!this.root.inputs.bodySolarArrayFaces.includes('MINUS_Z')) {
        cube.geometry.faces[10].color = cube.geometry.faces[11].color =
          new THREE.Color(0xd2d2d2)
        cubeGeometry.elementsNeedUpdate = true
      }
    },

    thrusterManagement(thruster) {
      let vectorthruster = new THREE.Vector3(
        parseFloat(this.root.inputs.thrusterAxisInSatelliteFrameX),
        parseFloat(this.root.inputs.thrusterAxisInSatelliteFrameY),
        parseFloat(this.root.inputs.thrusterAxisInSatelliteFrameZ)
      )
      let vectorabsthruster = new THREE.Vector3(
        parseFloat(Math.abs(this.root.inputs.thrusterAxisInSatelliteFrameX)),
        parseFloat(Math.abs(this.root.inputs.thrusterAxisInSatelliteFrameY)),
        parseFloat(Math.abs(this.root.inputs.thrusterAxisInSatelliteFrameZ))
      )

      let widthOutsideSpacecraft = this.basicLength / 10

      vectorthruster.normalize()
      vectorabsthruster.normalize()
      let scaleFactor = this.findDistanceToCubeSurface(
        vectorabsthruster,
        this.cubeX,
        this.cubeY,
        this.cubeZ,
        this.basicThrusterRadius,
        widthOutsideSpacecraft
      )
      let lambdap = Math.atan2(vectorthruster.y, vectorthruster.x)
      let phip = -Math.asin(vectorthruster.z)

      thruster.rotation.x = 0
      thruster.rotation.y = 0
      thruster.rotation.z = 0

      let q1p = new THREE.Quaternion()
      q1p.setFromAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 2)
      let q2p = new THREE.Quaternion()
      q2p.setFromAxisAngle(new THREE.Vector3(0, 1, 0), phip)
      let q3p = new THREE.Quaternion()
      q3p.setFromAxisAngle(new THREE.Vector3(0, 0, 1), lambdap)

      let qp = q3p.multiply(q2p).multiply(q1p)
      thruster.setRotationFromQuaternion(qp)
      thruster.scale.y = scaleFactor / this.basicLength
    },

    findDistanceToCubeSurface(
      normalizedAbsoluteCoordinates,
      cubeX,
      cubeY,
      cubeZ,
      componentRadius,
      widthOutsideSpacecraft
    ) {
      let normalizedAbsX = normalizedAbsoluteCoordinates.x
      let normalizedAbsY = normalizedAbsoluteCoordinates.y
      let normalizedAbsZ = normalizedAbsoluteCoordinates.z

      //Calculate the distance to the instersection of the point (x,y,z) and the faces of the spacecraft
      let distToIntersectFaceX =
        normalizedAbsX != 0 ? cubeX / 2 / normalizedAbsX : Infinity
      let distToIntersectFaceY =
        normalizedAbsY != 0 ? cubeY / 2 / normalizedAbsY : Infinity
      let distToIntersectFaceZ =
        normalizedAbsZ != 0 ? cubeZ / 2 / normalizedAbsZ : Infinity

      let minimalDistanceToIntersection = Math.min(
        Math.min(distToIntersectFaceX, distToIntersectFaceY),
        distToIntersectFaceZ
      )
      let firstFaceIntersected = 0
      if (minimalDistanceToIntersection === distToIntersectFaceX) {
        firstFaceIntersected = normalizedAbsX
      } else {
        firstFaceIntersected =
          minimalDistanceToIntersection === distToIntersectFaceY
            ? normalizedAbsY
            : normalizedAbsZ
      }
      //additional distance so that the thruster is completely outside the satellite
      let h =
        componentRadius *
        (Math.sqrt(1 - firstFaceIntersected ** 2) / firstFaceIntersected)

      return minimalDistanceToIntersection + h + widthOutsideSpacecraft
    },

    cameraAndWindowManagement(canvas, camera, renderer) {
      const width = canvas.offsetWidth
      const height = 400
      renderer.setSize(width, height)
      camera.aspect = width / height
      camera.updateProjectionMatrix()

      // Gestion de la camera pour avoir un bon ratio//
      camera.position.z = Math.max(
        Math.max(Math.max(this.cubeX, this.cubeY), this.cubeZ),
        height
      )
    },

    fixedPannelManagement(shaft, pannel) {
      var vectorShaft = new THREE.Vector3(
        parseFloat(this.root.inputs.solarArrayNormalInSatelliteFrameX),
        parseFloat(this.root.inputs.solarArrayNormalInSatelliteFrameY),
        parseFloat(this.root.inputs.solarArrayNormalInSatelliteFrameZ)
      )

      vectorShaft.normalize()
      shaft.scale.x = 1
      shaft.scale.y = 1
      shaft.scale.z = 1
      shaft.rotation.x = 0
      shaft.rotation.y = 0
      shaft.rotation.z = 0
      pannel.rotation.x = 0
      pannel.rotation.y = 0
      pannel.rotation.z = -Math.PI / 2
      pannel.rotation.reorder = 'ZYX'

      // rotations are here made arbitrary, based on hand-testing, due to the weird rotations of three.js

      shaft.rotation.z = Math.atan2(vectorShaft.y, vectorShaft.x) + Math.PI

      pannel.rotation.y = Math.asin(vectorShaft.z) - Math.PI / 2
    },

    create3DRenderer() {
      this.camera = new THREE.PerspectiveCamera(70, 2, 1, 2000)
      this.camera.position.z = 400
      this.scene = new THREE.Scene()

      // Objects sizes definition//

      const cubeVariables = [
        parseFloat(this.root.inputs.x),
        parseFloat(this.root.inputs.y),
        parseFloat(this.root.inputs.z)
      ]
      let minCubeVariable
      this.cubeX = this.basicLength
      this.cubeY = this.basicLength
      this.cubeZ = this.basicLength

      if (
        this.root.inputs.x !== 0 &&
        this.root.inputs.y !== 0 &&
        this.root.inputs.z !== 0
      ) {
        minCubeVariable = Math.min(
          parseFloat(this.root.inputs.x),
          parseFloat(this.root.inputs.y),
          parseFloat(this.root.inputs.z)
        )
      } else {
        minCubeVariable = 10
      }

      if (this.root.inputs.x !== '') {
        this.cubeX = this.basicLength * (cubeVariables[0] / minCubeVariable)
      }

      if (this.root.inputs.y !== '') {
        this.cubeY = this.basicLength * (cubeVariables[1] / minCubeVariable)
      }

      if (this.root.inputs.z !== '') {
        this.cubeZ = this.basicLength * (cubeVariables[2] / minCubeVariable)
      }

      const pannelX = this.basicLength
      const pannelY = 80
      const pannelZ = 5

      const thrusterDiameter = 2 * this.basicThrusterRadius
      const thrusterLength = this.basicLength

      const shaftDiameter = 2 * this.basicShaftRadius
      const shaftLength = this.basicLength

      const payloadDiameter = 2 * this.basicPayloadRadius
      const payloadLength = this.basicLength

      const antennaDiameter = 2 * this.basicAntennaRadius
      const antennaLength = this.basicLength

      // Création des géométries//

      this.cubeGeometry = new THREE.BoxGeometry(
        this.cubeX,
        this.cubeY,
        this.cubeZ
      )
      const pannelGeometry = new THREE.BoxGeometry(pannelX, pannelY, pannelZ)
      const thrusterGeometry = new THREE.CylinderGeometry(
        thrusterDiameter,
        thrusterDiameter,
        thrusterLength,
        32
      )
      thrusterGeometry.translate(0, thrusterLength / 2, 0)
      const shaftGeometry = new THREE.CylinderGeometry(
        shaftDiameter,
        shaftDiameter,
        shaftLength,
        32
      )
      shaftGeometry.translate(0, shaftLength / 2, 0)
      const payloadGeometry = new THREE.CylinderGeometry(
        payloadDiameter,
        1,
        payloadLength,
        32
      )
      payloadGeometry.translate(0, payloadLength / 2, 0)
      const antennaGeometry = new THREE.CylinderGeometry(
        antennaDiameter,
        1,
        antennaLength,
        32
      )
      antennaGeometry.translate(0, antennaLength / 2, 0)

      // Création des matériaux//

      const material = this.createBasicMaterial()
      const payloadMaterial = this.createPayloadMaterial()
      const antennaMaterial = this.createAntennaMaterial()
      const thrusterMaterial = this.createThrusterMaterial()
      const pannelMaterial = this.createPannelMaterial()

      // Création des objets
      this.cube = new THREE.Mesh(this.cubeGeometry, material)
      this.thruster = new THREE.Mesh(thrusterGeometry, thrusterMaterial)
      this.payload = new THREE.Mesh(payloadGeometry, payloadMaterial)
      this.antenna = new THREE.Mesh(antennaGeometry, antennaMaterial)
      this.shaft = new THREE.Mesh(shaftGeometry, material)
      this.pannel = new THREE.Mesh(pannelGeometry, pannelMaterial)
      this.scene.add(this.cube)

      this.cube.add(this.thruster)
      this.cube.add(this.payload)
      this.cube.add(this.antenna)
      this.cube.add(this.shaft)
      this.shaft.add(this.pannel)
      this.pannel.position.y = 220
      this.pannel.rotation.z = Math.PI / 2
      this.cube.rotation.x = -1
      this.cube.rotation.z = -2.5

      const xDir = new THREE.Vector3(1, 0, 0)
      xDir.normalize()
      const yDir = new THREE.Vector3(0, 1, 0)
      yDir.normalize()
      const zDir = new THREE.Vector3(0, 0, 1)
      zDir.normalize()

      const origin = new THREE.Vector3(0, 0, 0)
      const xArrow = new THREE.ArrowHelper(
        xDir,
        origin,
        this.cubeX / 2 + this.lengthArrowOutsideSpacecraft,
        this.xColor,
        this.tipArrowSize
      )

      const yArrow = new THREE.ArrowHelper(
        yDir,
        origin,
        this.cubeY / 2 + this.lengthArrowOutsideSpacecraft,
        this.yColor,
        this.tipArrowSize
      )
      const zArrow = new THREE.ArrowHelper(
        zDir,
        origin,
        this.cubeZ / 2 + this.lengthArrowOutsideSpacecraft,
        this.zColor,
        this.tipArrowSize
      )

      this.cube.add(xArrow)
      this.cube.add(yArrow)
      this.cube.add(zArrow)

      const fontJson = require('../../../node_modules/three/examples/fonts/helvetiker_regular.typeface.json')
      const font = new THREE.Font(fontJson)
      const parameters = {
        font: font,
        size: 15,
        height: 1,
        curveSegments: 1,
        bevelEnabled: false
      }

      var textGeometryX = new THREE.TextGeometry('X', parameters)
      var textMaterialX = new THREE.MeshPhongMaterial({
        color: this.xColor,
        specular: 0xffffff
      })
      var textGeometryY = new THREE.TextGeometry('Y', parameters)
      var textMaterialY = new THREE.MeshPhongMaterial({
        color: this.yColor,
        specular: 0xffffff
      })
      var textGeometryZ = new THREE.TextGeometry('Z', parameters)
      var textMaterialZ = new THREE.MeshPhongMaterial({
        color: this.zColor,
        specular: 0xffffff
      })

      var axisNameX = new THREE.Mesh(textGeometryX, textMaterialX)
      var axisNameY = new THREE.Mesh(textGeometryY, textMaterialY)
      var axisNameZ = new THREE.Mesh(textGeometryZ, textMaterialZ)
      axisNameX.position.set(
        this.axisNameOffset,
        this.cubeX / 2 + this.lengthArrowOutsideSpacecraft - this.tipArrowSize,
        0
      )
      axisNameY.position.set(
        this.axisNameOffset,
        this.cubeY / 2 + this.lengthArrowOutsideSpacecraft - this.tipArrowSize,
        0
      )
      axisNameZ.position.set(
        this.axisNameOffset,
        this.cubeZ / 2 + this.lengthArrowOutsideSpacecraft - this.tipArrowSize,
        0
      )
      xArrow.add(axisNameX)
      yArrow.add(axisNameY)
      zArrow.add(axisNameZ)

      const light1 = new THREE.SpotLight(0xffffff, 1.5, 0)
      light1.position.set(400, 200, 600)
      this.scene.add(light1)

      // Moving object with the mouse
      this.mouseMovingCube(this.cube, this.renderer)
      document
        .getElementById('3D_renderer')
        .appendChild(this.renderer.domElement)
        .setAttribute('id', '3DModel')

      let canvas = document.getElementById('3D_renderer')
      this.cameraAndWindowManagement(canvas, this.camera, this.renderer)
      this.payloadManagement(this.payload)
      this.antennaManagement(this.antenna)
      this.thrusterManagement(this.thruster)

      if (this.root.inputs.solarArrayType === 'SOLAR_ARRAY_TYPE_NONE') {
        for (let i = 0; i < 12; i++) {
          this.resetColor(i)
        }
        this.shaft.scale.x = 0.01
        this.shaft.scale.y = 0.01
        this.shaft.scale.z = 0.01
      }

      if (
        this.root.inputs.solarArrayType === 'SOLAR_ARRAY_TYPE_DEPLOYABLE_ROTATING'
      ) {
        for (let i = 0; i < 12; i++) {
          this.resetColor(i)
        }
        this.pannelRotatingManagement(this.shaft, this.pannel, this.requestId)
      }

      if (this.root.inputs.solarArrayType === 'SOLAR_ARRAY_TYPE_DEPLOYABLE_FIXED') {
        for (let i = 0; i < 12; i++) {
          this.resetColor(i)
        }
        this.fixedPannelManagement(this.shaft, this.pannel)
      }

      if (this.root.inputs.solarArrayType === 'SOLAR_ARRAY_TYPE_BODY') {
        this.shaft.scale.x = 0.01
        this.shaft.scale.y = 0.01
        this.shaft.scale.z = 0.01
        this.pannelBodyManagement(this.cube, this.cubeGeometry)
      }

      this.requestId = requestAnimationFrame(this.animate)
    },

    resetColor(i) {
      this.cubeGeometry.faces[i].color = new THREE.Color(0xd2d2d2)
      this.cubeGeometry.elementsNeedUpdate = true
    },

    animate(time) {
      time *= 0.001
      if (
        this.root.inputs.solarArrayType === 'SOLAR_ARRAY_TYPE_DEPLOYABLE_ROTATING'
      ) {
        this.pannel.rotation.y = time
      } else {
        // leave rotation as is, no animation nor changing the value set by the other  management methods
      }
      this.renderer.render(this.scene, this.camera)
      this.requestId = requestAnimationFrame(this.animate)
    },

    /**
     * This function centers the spacecraft and rotates it to match the orientation used in ExoOps Engine
     */
    resetModelPositionAndOrientation() {
      // this.cube.geometry.computeBoundingBox()
      // center the cube
      this.cube.position = new THREE.Vector3(0, 0, 0)
      // reset orientation
      this.cube.quaternion = new THREE.Quaternion()

      this.cube.updateMatrixWorld()

      const roll_axis = new THREE.Vector3(1, 0, 0)
      const roll = -Math.PI / 2

      const pitch_axis = new THREE.Vector3(0, 1, 0)
      const pitch = -Math.PI / 2

      // initialize quaternions
      const q_roll = new THREE.Quaternion()
      q_roll.setFromAxisAngle(roll_axis, roll)

      const q_pitch = new THREE.Quaternion()
      q_pitch.setFromAxisAngle(pitch_axis, pitch)

      // apply rotations to model
      this.cube.applyQuaternion(q_roll)
      this.cube.applyQuaternion(q_pitch)
    },

    async exportGLTF(download = false) {
      const exporter = new GLTFExporter()
      this.resetModelPositionAndOrientation()

      // This could be used to center object and its children. Here we want the cube at the center of the scene
      /* new THREE.Box3()
        .setFromObject(this.cube)
        .getCenter(this.cube.position)
        .multiplyScalar(-1) */

      // Parse the input and generate the glTF output
      exporter.parse(
        this.scene,
        async (result) => {
          if (result instanceof ArrayBuffer) {
            let formData = new FormData()

            formData.append(
              'shapeId',
              this.root.inputs.id.length > 1
                ? this.root.inputs.id
                : this.savedShapeId
            )
            formData.append(
              'shapeBinary',
              // 'cest une string'
              new Blob([result], { type: 'application/octet-stream' })
            )
            this.$http
              .post(`/users/${this.currentUser.id}/savethreedmodel`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              })
              .catch((e) => {
                console.warn('Error saving model', e, 'notification', 'error')
              })
            if (download === true) {
              this.saveArrayBuffer(result, 'scene.glb')
            }
          } else {
            const output = JSON.stringify(result, null, 2)
            this.saveString(output, 'scene.gltf')
          }
        },
        {
          // embedImages: true,
          binary: true,
          onlyVisible: true
          // trs: true
        }
      )
    },

    save(blob, filename) {
      const link = document.createElement('a')
      link.style.display = 'none'
      document.body.appendChild(link)
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()

      // URL.revokeObjectURL( url ); breaks Firefox...
    },

    saveString(text, filename) {
      this.save(new Blob([text], { type: 'text/plain' }), filename)
    },
    saveArrayBuffer(buffer, filename) {
      this.save(
        new Blob([buffer], { type: 'application/octet-stream' }),
        filename
      )
    }
  }
}
</script>
